import React, { useContext, useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { RemoveOutlined } from "@material-ui/icons";

import { DarkCell, InputCell, LightCell, OutputCell } from "./styles";

import { FORECAST_TABLE_PARAMS, MAX_INPUT_VALUE } from "../../constants";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_INCLUDED_STOCKPILE_DETAILS,
  GET_LIST_OF_INCLUDED_STOCKPILES,
} from "../../queries/stockpiles.gql";
import { MatchContext, UserInfo } from "../../context";
import { SET_ACTIVE_FORECAST, SET_DATA } from "../../reduce/actionTypes";
import { SEND_INCLUDE_IN_FORECAST } from "../../mutations/table-data.gql";
import TableCellErrorMessage from "../common/table-cell-error-message.component";
import AverageRowData from "../average-row-data/average-row-data.component";
import LoaderBar from "../common/loader-bar.component";

const Input = ({ value, col, feature, handleInputChange }): JSX.Element => {
  const { state } = useContext(MatchContext);
  let classes = " numInput";
  if (
    feature !== FORECAST_TABLE_PARAMS.SP &&
    feature !== FORECAST_TABLE_PARAMS.numberOfDays
  ) {
    classes += " widerInput";
  }

  let errTag = `${`${col}-${feature}`.toUpperCase()}-forecasting`;

  if (state.forecastInputErrors[errTag]) {
    classes += " errInput";
  }

  return (
    <input
      className={classes}
      type="number"
      value={value || 0}
      onChange={(e) => handleInputChange(col, feature, e.target.value)}
    />
  );
};

const ForecastTable = ({ activeData }): JSX.Element => {
  const { state, dispatch } = useContext(MatchContext);
  const { userId, username } = useContext(UserInfo);
  const [chosenRow, setChosenRow] = useState(null);

  const [getStockpileDetails, { loading: detailsLoading }] = useLazyQuery(
    GET_INCLUDED_STOCKPILE_DETAILS,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        const newRows = [...state.forecastTableRows];

        // replace on chosen row and delete symbols from prop `SP`
        newRows.splice(Number(chosenRow), 1, {
          ...data.getIncludedStockpileDetails,
          SP: data.getIncludedStockpileDetails.SP.replace(/SP_/i, "").replace(
            /SP /i,
            ""
          ),
        });

        dispatch({
          type: SET_DATA,
          payload: { forecastTableRows: newRows },
        });
      },
    }
  );
  const [getList, { data: listOfStockpiles, loading: listLoading }] =
    useLazyQuery(GET_LIST_OF_INCLUDED_STOCKPILES, { fetchPolicy: "no-cache" });
  const [includeInForecast, { loading: sendLoading }] = useMutation(
    SEND_INCLUDE_IN_FORECAST
  );

  useEffect(() => {
    if (activeData?.getActiveForecast) {
      // get rid of readonly props
      const newRows = JSON.stringify(
        activeData.getActiveForecast.includedStockpiles
      );

      dispatch({
        type: SET_ACTIVE_FORECAST,
        payload: {
          forecastTableRows: newRows,
          forecastId: activeData.getActiveForecast.forecastId,
          forecastRequestId: activeData.getActiveForecast.id,
          forecastNotes: activeData.getActiveForecast.notes,
        },
      });
      getList();
    }
  }, [activeData]);

  // useEffect(() => {
  //   if (state.stockpileName && state.requestId) {
  //     includeInForecast({
  //       variables: {
  //         stockpileId: state.forecastedStockpile,
  //         requestId: state.requestId,
  //         userInfo: { userId, username },
  //         inputStockpile: state.stockpileName,
  //       },
  //     });
  //   }
  // }, [state.forecastedStockpile]);

  const handleGetListOfStockpiles = () => {
    getList();
  };

  const handleValidateForecastRows = (name: string, value: number) => {
    const key = `${name.toUpperCase()}-forecasting`;

    if (value <= 0) {
      dispatch({
        type: SET_DATA,
        payload: {
          forecastInputErrors: {
            ...state.forecastInputErrors,
            [key]: "Must be positive number",
          },
        },
      });
    } else if (state.forecastInputErrors[key]) {
      dispatch({
        type: SET_DATA,
        payload: {
          forecastInputErrors: {
            ...state.forecastInputErrors,
            [key]: null,
          },
        },
      });
    }
  };

  const handleInputChange = (col, feature, newValue) => {
    const newRows = [...state.forecastTableRows];
    const value = Number(newValue);
    newRows[col][feature] = value;
    if (Math.abs(value) <= MAX_INPUT_VALUE) {
      dispatch({
        type: SET_DATA,
        payload: { forecastTableRows: newRows },
      });
      handleValidateForecastRows(`${col}-${feature}`, value);
    }
  };

  const handlePlusClick = () => {
    const newRows = [...state.forecastTableRows];
    newRows.push({ id: Date.now() });
    dispatch({
      type: SET_DATA,
      payload: { forecastTableRows: newRows },
    });
  };

  const handleMinusClick = (index) => {
    if (state.forecastTableRows.length > 1) {
      const newRows = [...state.forecastTableRows];
      newRows.splice(index, 1);
      dispatch({
        type: SET_DATA,
        payload: { forecastTableRows: newRows },
      });
    } else {
      dispatch({
        type: SET_DATA,
        payload: { forecastTableRows: [{ id: Date.now() }] },
      });
    }
  };

  const handleSelectStockPileForecast = (value, index) => {
    const id = value.split("__")[1];
    const stockpile = value.split("__")[0];
    setChosenRow(index);
    getStockpileDetails({ variables: { id, stockpile } });
  };

  return (
    <TableContainer className="forecasting-table" component={Paper}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow className="bold">
            <LightCell align="center">Input SP</LightCell>
            <DarkCell align="center">Matched SP</DarkCell>
            <LightCell align="center"># of Days</LightCell>
            <LightCell align="center">{FORECAST_TABLE_PARAMS.Zn_Fd}</LightCell>
            <LightCell align="center">{FORECAST_TABLE_PARAMS.Pb_Fd}</LightCell>
            <DarkCell align="center">{FORECAST_TABLE_PARAMS.Zn_Rec}</DarkCell>
            <DarkCell align="center">{FORECAST_TABLE_PARAMS.Zn_Gr}</DarkCell>
            <DarkCell align="center">{FORECAST_TABLE_PARAMS.Pb_Rec}</DarkCell>
            <DarkCell align="center">{FORECAST_TABLE_PARAMS.Pb_Gr}</DarkCell>
            <LightCell align="center">{FORECAST_TABLE_PARAMS.TPOH}</LightCell>
            <LightCell>
              <div className="lock"></div>
            </LightCell>
          </TableRow>
        </TableHead>
        {state.forecastTableRows.length && (
          <TableBody>
            {state.forecastTableRows.map((row, index) => (
              <TableRow key={index} id={index}>
                <InputCell>
                  <select
                    name="stockpiles"
                    className="forecast-sp-select"
                    onChange={(e) =>
                      handleSelectStockPileForecast(e.target.value, index)
                    }
                    onClick={handleGetListOfStockpiles}
                  >
                    {!row.inputStockpile ? (
                      <option value="-">{""}</option>
                    ) : (
                      <option value={row.inputStockpile}>
                        {row.inputStockpile}
                      </option>
                    )}
                    {listOfStockpiles?.getListOfIncludedStockpiles
                      .filter((item) => {
                        return !state.forecastTableRows.some((f) => {
                          return f.inputStockpile === item.stockpile;
                        });
                      })
                      .map((item) => {
                        return (
                          <option
                            value={`${item.stockpile}__${item.id}`}
                            key={item.id}
                          >
                            {item.stockpile}
                          </option>
                        );
                      })}
                  </select>
                </InputCell>
                <React.Fragment>
                  <OutputCell align="center">
                    {row[FORECAST_TABLE_PARAMS.SP] || 0}
                  </OutputCell>
                  <InputCell align="center">
                    <Input
                      value={row[FORECAST_TABLE_PARAMS.numberOfDays]}
                      col={index}
                      feature={FORECAST_TABLE_PARAMS.numberOfDays}
                      handleInputChange={handleInputChange}
                    />
                    <TableCellErrorMessage
                      feature={`${index}-${FORECAST_TABLE_PARAMS.numberOfDays}`}
                      type="forecasting"
                      errors={state.forecastInputErrors}
                    />
                  </InputCell>
                  <InputCell align="center">
                    <Input
                      value={row[FORECAST_TABLE_PARAMS.Zn_Fd]}
                      col={index}
                      feature={FORECAST_TABLE_PARAMS.Zn_Fd}
                      handleInputChange={handleInputChange}
                    />
                    <TableCellErrorMessage
                      feature={`${index}-${FORECAST_TABLE_PARAMS.Zn_Fd}`}
                      type="forecasting"
                      errors={state.forecastInputErrors}
                    />
                  </InputCell>
                  <InputCell align="center">
                    <Input
                      value={row[FORECAST_TABLE_PARAMS.Pb_Fd]}
                      feature={FORECAST_TABLE_PARAMS.Pb_Fd}
                      col={index}
                      handleInputChange={handleInputChange}
                    />
                    <TableCellErrorMessage
                      feature={`${index}-${FORECAST_TABLE_PARAMS.Pb_Fd}`}
                      type="forecasting"
                      errors={state.forecastInputErrors}
                    />
                  </InputCell>
                  <OutputCell align="center">
                    {(row[FORECAST_TABLE_PARAMS.Zn_Rec] &&
                      Number(row[FORECAST_TABLE_PARAMS.Zn_Rec]).toFixed(2)) ||
                      0}
                  </OutputCell>
                  <OutputCell align="center">
                    {(row[FORECAST_TABLE_PARAMS.Zn_Gr] &&
                      Number(row[FORECAST_TABLE_PARAMS.Zn_Gr]).toFixed(2)) ||
                      0}
                  </OutputCell>
                  <OutputCell align="center">
                    {(row[FORECAST_TABLE_PARAMS.Pb_Rec] &&
                      Number(row[FORECAST_TABLE_PARAMS.Pb_Rec]).toFixed(2)) ||
                      0}
                  </OutputCell>
                  <OutputCell align="center">
                    {(row[FORECAST_TABLE_PARAMS.Pb_Gr] &&
                      Number(row[FORECAST_TABLE_PARAMS.Pb_Gr]).toFixed(2)) ||
                      0}
                  </OutputCell>
                  <InputCell align="center">
                    <Input
                      value={
                        Math.round(
                          (Number(row[FORECAST_TABLE_PARAMS.TPOH]) +
                            Number.EPSILON) *
                            100
                        ) / 100
                      }
                      col={index}
                      feature={FORECAST_TABLE_PARAMS.TPOH}
                      handleInputChange={handleInputChange}
                    />
                    <TableCellErrorMessage
                      feature={`${index}-${FORECAST_TABLE_PARAMS.TPOH}`}
                      type="forecasting"
                      errors={state.forecastInputErrors}
                    />
                  </InputCell>
                </React.Fragment>
                <InputCell>
                  <i className="material-icon">
                    <RemoveOutlined onClick={() => handleMinusClick(index)} />
                  </i>
                </InputCell>
              </TableRow>
            ))}
            <TableRow className="bold">
              <AverageRowData handlePlusClick={handlePlusClick} />
            </TableRow>
          </TableBody>
        )}
      </Table>
      {detailsLoading || listLoading || sendLoading ? (
        <LoaderBar />
      ) : (
        <span></span>
      )}
    </TableContainer>
  );
};

export default ForecastTable;
